import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, inject, Injectable, OnDestroy, OnInit, ViewChild } from '@angular/core'
import { DocumentsStore, ItemWithID } from '../../stores/document.store'
import { Dictionary, NamedSearchParameters } from '../filtering-search-component-base/filtering-search-types'
import { AdobePDFViewerComponent } from '../adobe-pdf-viewer/adobe-pdf-viewer'
import { switchMap, take, tap, withLatestFrom } from 'rxjs'
import { User } from 'src/app/state/user.model'
import { InputDialogComponent, InputDialogStepComponent } from '../input-dialog/input-dialog.component'
import { updateUserNextGenCSO } from 'src/app/state/user.actions'
import { NgxBootstrapIconsModule } from 'ngx-bootstrap-icons'
import { environment } from 'src/environments/environment'
import { FormsModule } from '@angular/forms'
import { RouterLink } from '@angular/router'
import { NgbProgressbar } from '@ng-bootstrap/ng-bootstrap'
import { NgTemplateOutlet, AsyncPipe, DecimalPipe, DatePipe } from '@angular/common'
import { InfiniteScrollDirective } from 'ngx-infinite-scroll'
import { NgScrollbarModule, ScrollViewport } from 'ngx-scrollbar'
import { LocalFilteringSearchComponent } from '../document-filterable-search/document-filterable-search-component'
import { DocumentLinksComponent } from '../document-links/document-links.component'
import { DocumentsViewerBase } from '../documents-viewer-base/documents-viewer-base.component'
import { tapResponse } from '@ngrx/operators'

export interface CourtDocumentEntry {
  document_id: number
  description: string
  our_path: string
}

export interface CourtDocument extends ItemWithID {
  docketid: string
  rssid: number
  description: string
  case_name: string
  case_number: string
  documenttypes: string[]
  full_description: string
  dominantindustry: string
  docs: CourtDocumentEntry[]
  date: string
  id: number
  matched_documents: any,
  casedocumentinfo: Dictionary<any>
  purchaseable: boolean
  purchasingToken: string | undefined
}

@Injectable()
export class CourtDocumentsStore extends DocumentsStore<CourtDocument> {
  readonly setDocumentPurchasingToken = this.effect<{ id: number, purchasingToken: string }>(
    (trigger$) => trigger$.pipe(
      tap(({ id, purchasingToken }) => {
        const state = this.get()
        this.patchState({
          ...state,
          entities: {
            ...state.entities,
            [id]: {
              ...state.entities[id],
              purchasingToken,
              purchaseable: purchasingToken.length > 0
            }
          }
        })
      })
    )
  )
  readonly clearDocumentPurchasingToken = this.effect<{ id: number }>(
    (trigger$) => trigger$.pipe(
      tap(({ id }) => {
        const state = this.get()
        this.patchState({
          ...state,
          entities: {
            ...state.entities,
            [id]: {
              ...state.entities[id],
              purchasingToken: '',
              purchaseable: true
            }
          }
        })
      })
    )
  )

  readonly updateDocumentAfterPurchase = this.effect<number>(
    (trigger$) => trigger$.pipe(
      withLatestFrom(this.select(state => state)),
      switchMap(([id, state]) => {
        return this.backendService.search({
          area: 'documents',
          parameters: {
            rssid: {
              value: state.entities[id]?.rssid
            }
          },
          window: {
            start: 1,
            size: 1,
          },
        }).pipe(
          tapResponse(
            (result: any) => {
              result.articles[0].id = id
              state = this.adapter.upsertMany(result.articles, state)
              return this.patchState({ ...state, loading: false, error: false }) //, listing: result.listing })
            },
            (error) => this.patchState({ loading: false, error: true }),
          )
        )
      })
    )
  )
}



@Component({
    selector: 'documents-viewer',
    templateUrl: './documents-viewer.component.html',
    styleUrls: ['../documents-viewer-base/documents-viewer-base.component.scss'],
    providers: [CourtDocumentsStore],
    changeDetection: ChangeDetectionStrategy.Default,
    imports: [
        LocalFilteringSearchComponent,
        NgScrollbarModule,
        InfiniteScrollDirective,
        ScrollViewport,
        NgTemplateOutlet,
        NgbProgressbar,
        RouterLink,
        AdobePDFViewerComponent,
        InputDialogComponent,
        InputDialogStepComponent,
        FormsModule,
        NgxBootstrapIconsModule,
        AsyncPipe,
        DecimalPipe,
        DatePipe,
        DocumentLinksComponent
    ]
})
export class DocumentsViewerComponent extends DocumentsViewerBase<CourtDocument> implements OnInit, OnDestroy {

  @ViewChild(AdobePDFViewerComponent) adobePDFViewerComponent: AdobePDFViewerComponent | undefined

  @ViewChild('updateDocketDialog') updateDocketComponent: InputDialogComponent = {} as InputDialogComponent
  @ViewChild('updateDocketForm') updateDocketForm: ElementRef = {} as ElementRef

  public pacerUsernameRequired: boolean = true
  public pacerPasswordRequired: boolean = true
  public pacerLoginButtonEnabled = true
  public pdfPurchase: Dictionary<any> = {}
  public pdfPurchaseIntervalTimer = 0

  public pdfPurchaseInterval = 1000

  public docketUpdating = ''
  public docketUpdateIntervalTimer = 0

  public pacerUsername = ''
  public pacerPassword = ''
  public pacerCredentialsWrong = false

  public cdr = inject(ChangeDetectorRef)

  constructor() {
    super(inject(CourtDocumentsStore))
  }


  override ngOnInit(): void {
    super.ngOnInit()
    if (this.embedded) {
      const docketIDToPurchase = this.route.snapshot.queryParamMap.get('purchase') ?? ''
      if (!isNaN(parseInt(docketIDToPurchase))) {
        const documentsSubscription$ = this.documents$.subscribe((documents: CourtDocument[]) => {
          for (const doc of documents) {
            if (doc.docketid == docketIDToPurchase) {
              this.purchaseDocument(doc)
              documentsSubscription$.unsubscribe()
            }
          }
        })
      }
    }
  }

  ngOnDestroy(): void {
    clearInterval(this.pdfPurchaseIntervalTimer)
    clearInterval(this.docketUpdateIntervalTimer)
  }


  reloadDocket(loaded: boolean, id: number) {
    if (loaded) {
      (this.documentsStore as CourtDocumentsStore).updateDocumentAfterPurchase(id)
    }
  }


  checkDocketUpdateStatus(token: string) {
    this.backendService.checkJobStatus(token).pipe(take(1)).subscribe((result: any) => {
      if (result.response.status == 'finished') {
        this.docketUpdating = ''
        window.clearInterval(this.docketUpdateIntervalTimer)
        this.toastService.show('Docket updated', {
          header: 'Docket Updated',
          classname: 'bg-success',
          autohide: true,
          delay: 5000,
        })
        this.documentsStore.removeAll()
        this.documentsStore.getDocuments()
      } else {
        this.docketUpdating = result.response.status
      }
      this.cdr.detectChanges()
    })
  }


  convertFormat(document: CourtDocument): any {
    let result = structuredClone(document) as any
    result.documents = result.matched_documents.map((doc: any) => {
      doc.description = doc.doc_description
      return doc
    })
    return result
  }

  displayPDF(cdn_path: string) {
    let search: NamedSearchParameters = {}
    this.search$.subscribe((s: NamedSearchParameters) => {
      search = s
    })

    this.adobePDFViewerComponent!.displayPDF({
      id: 1,
      cdn_path: environment.baseUrl + cdn_path
    }, search?.parameters?.['content']?.value)
  }


  setupPacerCredentialsForm() {
    const form: HTMLFormElement = (this.updateDocketForm.nativeElement as HTMLFormElement)
    form.classList.remove('was-validated')
    this.pacerCredentialsWrong = false
    this.pacerUsernameRequired = false
    this.pacerPasswordRequired = false
    this.pacerLoginButtonEnabled = true
  }

  purchaseDocument(document: CourtDocument) {
    this.setupPacerCredentialsForm()

    let user: User | undefined = undefined
    this.user$.subscribe(u => user = u!)

    if (user!.nextGenCSO) {
      this.backendService.validatePacerToken(user!.nextGenCSO).subscribe((result) => {
        this.updateDocketComponent.showModal()
      })
    } else {
      this.updateDocketComponent.showModal()
    }
  }

  startDocketUpdateJob(nextGenCSO: string) {
    this.backendService.startDocketUpdateProcess({
      request: {
        nextGenCSO,
        ...this.requiredFilters,
      }
    }).subscribe((result) => {
      this.docketUpdating = 'Updating...'
      this.docketUpdateIntervalTimer = window.setInterval(() => this.checkDocketUpdateStatus(result.token), this.pdfPurchaseInterval)
    })
  }

  updateDocket() {
    this.setupPacerCredentialsForm()
    let user: User | undefined = undefined
    this.user$.subscribe(u => user = u!)

    if (user!.nextGenCSO) {
      this.backendService.validatePacerToken(user!.nextGenCSO).subscribe((result) => {
        if (result.ok) {
          this.startDocketUpdateJob(user!.nextGenCSO!)
        } else {
          this.updateDocketComponent.showModal()
        }
      })
    } else {
      this.updateDocketComponent.showModal()
    }
  }

  checkPacerLogin() {
    const form: HTMLFormElement = (this.updateDocketForm.nativeElement as HTMLFormElement)
    form.classList.add('was-validated')
    this.pacerUsernameRequired = this.pacerUsername?.length < 5
    this.pacerPasswordRequired = this.pacerPassword?.length < 5
    this.pacerCredentialsWrong = false
    this.pacerLoginButtonEnabled = false

    let problem = this.pacerUsernameRequired || this.pacerPasswordRequired
    if (!problem) {
      this.backendService.pacerLogin(this.pacerUsername, this.pacerPassword).subscribe((result) => {
        if (result.pacer.loginResult === "0") {
          this.store.dispatch(updateUserNextGenCSO(result.pacer))
          this.updateDocketComponent.advanceToNextStep()
          this.startDocketUpdateJob(result.pacer.nextGenCSO)
        } else {
          this.pacerCredentialsWrong = true
          this.pacerLoginButtonEnabled = true
          this.cdr.detectChanges()
        }
      })
    }
    this.pacerLoginButtonEnabled = problem
    return problem
  }

  getBracketedWords(description: string) {
    return description.replace(/.*\[(.*)\].*/, '$1')
  }

}